import { COVERAGE_BLOCK } from 'src/components/star-grid/datasets/datasetConstants';
import type { DatasetColumnHierarchy, HierarchyCandidate, ProfileColumn } from 'src/components/star-grid/datasets/datasetTypes';

/** Pairs whose dimension or hierarchy contains the query (case-insensitive); all of them when the query is empty. */
export function filterHierarchyPairs(pairs: readonly DatasetColumnHierarchy[], query: string): DatasetColumnHierarchy[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...pairs];
  return pairs.filter((pair) => pair.dimension.toLowerCase().includes(needle) || pair.hierarchy.toLowerCase().includes(needle));
}

/**
 * A dimension's confirmed hierarchy choice: flattened, or a `(dimension, hierarchy)` pair. A candidate
 * carries its coverage; a hand-picked pair carries `null`, since its coverage was never computed.
 */
export type HierarchyChoice =
  | { readonly kind: 'flat' }
  | { readonly kind: 'hierarchy'; readonly dimension: string; readonly hierarchy: string; readonly coverage: number | null };

/** The ranked matches for a dimension: the new `hierarchy_candidates`, falling back to the single legacy suggestion. */
export function columnHierarchyCandidates(column: ProfileColumn): HierarchyCandidate[] {
  if (column.hierarchy_candidates && column.hierarchy_candidates.length > 0) return column.hierarchy_candidates;
  if (column.suggested_hierarchy) return [{ ...column.suggested_hierarchy, matched_on: '' }];
  return [];
}

/** The choice in effect for a dimension: the user's pick, else the top candidate, else flat. */
export function effectiveHierarchyChoice(column: ProfileColumn, choices: Readonly<Record<string, HierarchyChoice>>): HierarchyChoice {
  const chosen = choices[column.name];
  if (chosen) return chosen;
  const [top] = columnHierarchyCandidates(column);
  // Only a match good enough to keep is pre-selected, so a weak suggestion never blocks Next.
  const keepable = top && (top.coverage === null || top.coverage >= COVERAGE_BLOCK);
  return keepable ? { kind: 'hierarchy', dimension: top.dimension, hierarchy: top.hierarchy, coverage: top.coverage } : { kind: 'flat' };
}

/** A choice blocks saving only when it keeps a hierarchy whose known coverage is below the block threshold. */
export function choiceBlocks(choice: HierarchyChoice, blockBelow: number): boolean {
  return choice.kind === 'hierarchy' && choice.coverage !== null && choice.coverage < blockBelow;
}
